import { Injectable } from 'framework/di';

import { AppService } from '../app/app.service';
import { FilterHelper } from './filter.helper';
import type { DynamicStickyController, StickyOverrideConfig } from './sticky-dynamic';
import {
  initializeStickyHorizontal,
  initializeStickyMobile,
  initializeStickyVertical,
  loadStickyFilterTreeExpandContent,
  loadStickyFilterTreeHorizontalContent,
  loadStickyFilterTreeHorizontalPanel,
  loadStickyFilterTreeMobileExpandContent,
} from './sticky-dynamic';

/** Small query helper */
const qs = <T extends Element = HTMLElement>(sel: string, root: Document | Element = document) =>
  root.querySelector<T>(sel);

/**
 * Extended type to support nested configurations for style-expand
 */
type ExtendedStickyOverrideConfig = StickyOverrideConfig & {
  toolbar?: StickyOverrideConfig;
  panel?: StickyOverrideConfig;
  body?: StickyOverrideConfig;
};

/**
 * Type for storing controller instances
 */
type StickyControllerInstance = {
  controller: DynamicStickyController;
  cleanup: () => void;
  layoutType: 'mobile' | 'vertical' | 'horizontal';
  layoutStyle: string;
  createdAt: number;
};

/**
 * Controller for managing sticky filter behavior across different layouts.
 *
 * Orchestrates sticky filter positioning and behavior for mobile, vertical,
 * and horizontal layouts. Manages multiple sticky controller instances,
 * configuration merging, and lifecycle cleanup. Supports customization
 * through global and layout-specific configuration overrides.
 *
 * @remarks
 * This controller handles:
 * - Multiple sticky controller instance management
 * - Configuration override merging (global + layout-specific)
 * - Filter opening state management with body class toggling
 * - Automatic cleanup of sticky instances and observers
 * - Layout-specific initialization (mobile/vertical/horizontal)
 *
 * Technical Support teams can extend this controller to customize sticky
 * behavior, add shop-specific positioning logic, or integrate custom
 * sticky implementations.
 *
 * @example
 * Extend to add custom sticky configuration:
 * ```javascript
 * window.boostWidgetIntegration.extend('FilterStickyController', (FilterStickyController) => {
 *   return class CustomStickyController extends FilterStickyController {
 *     getStickyConfig(layoutType, layoutStyle) {
 *       const config = super.getStickyConfig(layoutType, layoutStyle);
 *       // Add shop-specific sticky offsets
 *       return {
 *         ...config,
 *         merged: {
 *           ...config.merged,
 *           behavior: {
 *             ...config.merged?.behavior,
 *             topOffset: this.getCustomTopOffset()
 *           }
 *         }
 *       };
 *     }
 *   };
 * });
 * ```
 *
 * @example
 * Override initialization for custom layouts:
 * ```javascript
 * window.boostWidgetIntegration.extend('FilterStickyController', (FilterStickyController) => {
 *   return class extends FilterStickyController {
 *     async initialize() {
 *       // Add custom pre-initialization logic
 *       this.setupCustomStickyElements();
 *       const result = await super.initialize();
 *       // Track sticky initialization
 *       this.analytics.track('sticky_filter_initialized');
 *       return result;
 *     }
 *   };
 * });
 * ```
 */
@Injectable
export class FilterStickyController {
  private instancesStorage: Map<string, StickyControllerInstance>;
  private _filterOpeningObserver: MutationObserver | null = null;

  /**
   * Creates a new FilterStickyController instance.
   *
   * @param appService - Application service for accessing configuration and blocks
   * @param filterHelper - Helper utilities for filter operations and mobile detection
   */
  constructor(
    private appService: AppService,
    private filterHelper: FilterHelper
  ) {
    this.instancesStorage = new Map();
  }

  /**
   * Generates a unique key for storing controller instances.
   */
  private getInstanceKey(
    layoutType: 'mobile' | 'vertical' | 'horizontal',
    layoutStyle: string
  ): string {
    return `${layoutType}-${layoutStyle}`;
  }

  /**
   * Stores a sticky controller instance globally.
   * Automatically cleans up any existing instance with the same key.
   */
  private storeStickyInstance(
    layoutType: 'mobile' | 'vertical' | 'horizontal',
    layoutStyle: string,
    controller: DynamicStickyController,
    cleanup: () => void
  ): void {
    const key = this.getInstanceKey(layoutType, layoutStyle);

    // Cleanup existing instance if present
    const existing = this.instancesStorage.get(key);
    if (existing) {
      try {
        existing.cleanup();
      } catch (error) {
        console.warn(`[sticky] Error cleaning up existing instance for ${key}:`, error);
      }
    }

    // Store new instance
    this.instancesStorage.set(key, {
      controller,
      cleanup,
      layoutType,
      layoutStyle,
      createdAt: Date.now(),
    });
  }

  /**
   * Retrieves a sticky controller instance by layout type and style.
   *
   * Gets the stored controller instance for a specific layout configuration.
   * Returns undefined if no instance exists for the given layout.
   *
   * @param layoutType - The layout type ('mobile', 'vertical', 'horizontal')
   * @param layoutStyle - The specific style within that layout (e.g., 'style1', 'style-default')
   * @returns The sticky controller instance with cleanup function, or undefined if not found
   *
   * @example
   * Override to add custom instance retrieval logic:
   * ```typescript
   * getStickyInstance(layoutType, layoutStyle) {
   *   const instance = super.getStickyInstance(layoutType, layoutStyle);
   *   if (instance) {
   *     // Track instance retrieval
   *     this.logStickyInstanceAccess(layoutType, layoutStyle);
   *   }
   *   return instance;
   * }
   * ```
   */
  public getStickyInstance(
    layoutType: 'mobile' | 'vertical' | 'horizontal',
    layoutStyle: string
  ): StickyControllerInstance | undefined {
    const key = this.getInstanceKey(layoutType, layoutStyle);
    return this.instancesStorage.get(key);
  }

  /**
   * Gets all active sticky controller instances.
   *
   * Returns an array of all currently managed sticky controller instances
   * across all layouts and styles. Useful for debugging or bulk operations.
   *
   * @returns Array of all active sticky controller instances
   *
   * @example
   * Override to add instance monitoring:
   * ```typescript
   * getAllStickyInstances() {
   *   const instances = super.getAllStickyInstances();
   *   // Log instance count for monitoring
   *   this.analytics.track('sticky_instances', {
   *     count: instances.length,
   *     layouts: instances.map(i => i.layoutType)
   *   });
   *   return instances;
   * }
   * ```
   */
  public getAllStickyInstances(): StickyControllerInstance[] {
    return Array.from(this.instancesStorage.values());
  }

  /**
   * Cleans up a specific sticky instance by layout type and style.
   *
   * Removes and cleans up the sticky controller instance for a specific
   * layout configuration. Safe to call even if no instance exists.
   *
   * @param layoutType - The layout type to clean up
   * @param layoutStyle - The specific style to clean up
   *
   * @example
   * Override to add custom cleanup logic:
   * ```typescript
   * cleanupStickyInstance(layoutType, layoutStyle) {
   *   // Clean custom shop-specific elements first
   *   this.cleanupCustomStickyElements(layoutType);
   *   super.cleanupStickyInstance(layoutType, layoutStyle);
   * }
   * ```
   */
  public cleanupStickyInstance(
    layoutType: 'mobile' | 'vertical' | 'horizontal',
    layoutStyle: string
  ): void {
    const key = this.getInstanceKey(layoutType, layoutStyle);
    const instance = this.instancesStorage.get(key);

    if (instance) {
      try {
        instance.cleanup();
      } catch (error) {
        console.warn(`[sticky] Error cleaning up instance for ${key}:`, error);
      }
      this.instancesStorage.delete(key);
    }
  }

  /**
   * Cleans up all sticky controller instances.
   *
   * Removes and cleans up all managed sticky controller instances across
   * all layouts. Errors during cleanup are logged but don't prevent other
   * instances from being cleaned up.
   *
   * @remarks
   * Called automatically during controller cleanup. Safe to call multiple times.
   *
   * @example
   * Override to add bulk cleanup tracking:
   * ```typescript
   * cleanupAllStickyInstances() {
   *   const instanceCount = this.getAllStickyInstances().length;
   *   super.cleanupAllStickyInstances();
   *   // Track cleanup for monitoring
   *   this.analytics.track('all_sticky_instances_cleaned', {
   *     count: instanceCount
   *   });
   * }
   * ```
   */
  public cleanupAllStickyInstances(): void {
    for (const [key, instance] of this.instancesStorage.entries()) {
      try {
        instance.cleanup();
      } catch (error) {
        console.warn(`[sticky] Error cleaning up instance for ${key}:`, error);
      }
    }

    this.instancesStorage.clear();
  }

  /**
   * Gets filter settings from app configuration.
   *
   * Retrieves the complete filter settings object from AppService,
   * including sticky configuration, layout settings, and styles.
   *
   * @returns Filter settings object with sticky and layout configuration
   *
   * @example
   * Override to add custom settings:
   * ```typescript
   * getFilterSettings() {
   *   const settings = super.getFilterSettings();
   *   // Merge with custom shop settings
   *   return {
   *     ...settings,
   *     customStickyOffset: this.getShopStickyOffset()
   *   };
   * }
   * ```
   */
  public getFilterSettings() {
    return this.appService.filterSettings;
  }

  /**
   * Gets sticky status from filter settings.
   *
   * Determines whether sticky behavior is enabled for mobile, desktop,
   * or both. Returns a status object with individual flags.
   *
   * @returns Object containing sticky status flags:
   *   - stickyEnabled: True if enabled on mobile OR desktop
   *   - stickyFilterOnMobile: Mobile sticky flag
   *   - stickyFilterOnDesktop: Desktop sticky flag
   *
   * @example
   * Override to add custom sticky detection:
   * ```typescript
   * getStickyStatus() {
   *   const status = super.getStickyStatus();
   *   // Add custom conditions
   *   return {
   *     ...status,
   *     stickyEnabled: status.stickyEnabled && this.isCustomConditionMet()
   *   };
   * }
   * ```
   */
  public getStickyStatus() {
    const { stickyFilterOnMobile, stickyFilterOnDesktop } = this.getFilterSettings();
    return {
      stickyEnabled: stickyFilterOnDesktop || stickyFilterOnMobile,
      stickyFilterOnMobile,
      stickyFilterOnDesktop,
    };
  }

  /**
   * Merges global sticky override with layout-specific override.
   *
   * Combines global and layout-specific sticky configuration overrides,
   * with layout-specific settings taking precedence. Performs deep merge
   * of elements, styling, behavior, and hooks.
   *
   * @param globalOverride - Global sticky configuration override
   * @param layoutSpecificOverride - Layout-specific configuration override
   * @returns Merged configuration, or undefined if both are undefined
   *
   * @remarks
   * Layout-specific overrides take precedence over global ones in all
   * nested properties. Debug flag from layout-specific overrides has priority.
   *
   * @example
   * Override to add custom merge logic:
   * ```typescript
   * mergeOverrides(globalOverride, layoutSpecificOverride) {
   *   const merged = super.mergeOverrides(globalOverride, layoutSpecificOverride);
   *   if (merged) {
   *     // Add shop-specific defaults
   *     merged.behavior = {
   *       ...merged.behavior,
   *       customShopBehavior: this.getShopStickyBehavior()
   *     };
   *   }
   *   return merged;
   * }
   * ```
   */
  public mergeOverrides(
    globalOverride?: StickyOverrideConfig,
    layoutSpecificOverride?: StickyOverrideConfig
  ): StickyOverrideConfig | undefined {
    if (!globalOverride && !layoutSpecificOverride) {
      return undefined;
    }

    if (!globalOverride) {
      return layoutSpecificOverride;
    }

    if (!layoutSpecificOverride) {
      return globalOverride;
    }

    // Deep merge with layout-specific taking precedence
    return {
      elements: {
        ...globalOverride.elements,
        ...layoutSpecificOverride.elements,
      },
      styling: {
        ...globalOverride.styling,
        ...layoutSpecificOverride.styling,
      },
      behavior: {
        ...globalOverride.behavior,
        ...layoutSpecificOverride.behavior,
      },
      hooks: {
        ...globalOverride.hooks,
        ...layoutSpecificOverride.hooks,
      },
      debug: layoutSpecificOverride.debug ?? globalOverride.debug,
    };
  }

  /**
   * Gets global and layout-specific sticky overrides from configuration.
   */
  private getConfigOverrides(
    layoutType: 'mobile' | 'vertical' | 'horizontal',
    layoutStyle: string
  ): {
    globalOverride: StickyOverrideConfig | undefined;
    layoutSpecificOverride: StickyOverrideConfig | undefined;
  } {
    const config = window.boostWidgetIntegration?.config?.customization?.filter?.sticky;
    const globalOverride = (config as any)?.global;
    const layoutSpecificOverride = config?.[layoutType]?.[layoutStyle];
    return { globalOverride, layoutSpecificOverride };
  }

  /**
   * Gets merged sticky configuration for any layout type.
   *
   * Retrieves and merges global and layout-specific sticky configuration,
   * including specialized configs for nested elements (panel, content, toolbar).
   * Handles extended configuration with nested overrides.
   *
   * @param layoutType - The layout type ('mobile', 'vertical', 'horizontal')
   * @param layoutStyle - The specific style within that layout
   * @returns Object containing:
   *   - merged: Complete merged configuration
   *   - panel: Configuration for panel element (or merged if not specified)
   *   - content: Configuration for content/body element (or merged if not specified)
   *   - toolbar: Configuration for toolbar element (or merged if not specified)
   *
   * @remarks
   * Extended configurations can define nested panel, body, and toolbar
   * overrides for fine-grained control of sticky behavior.
   *
   * @example
   * Override to add custom configuration processing:
   * ```typescript
   * getStickyConfig(layoutType, layoutStyle) {
   *   const config = super.getStickyConfig(layoutType, layoutStyle);
   *   // Add shop-specific configuration adjustments
   *   return {
   *     ...config,
   *     merged: {
   *       ...config.merged,
   *       behavior: {
   *         ...config.merged?.behavior,
   *         topOffset: this.calculateCustomOffset(layoutType)
   *       }
   *     }
   *   };
   * }
   * ```
   */
  public getStickyConfig(layoutType: 'mobile' | 'vertical' | 'horizontal', layoutStyle: string) {
    const { globalOverride, layoutSpecificOverride } = this.getConfigOverrides(
      layoutType,
      layoutStyle
    );

    const mergedOverride = this.mergeOverrides(globalOverride, layoutSpecificOverride);
    const extendedOverride = mergedOverride as ExtendedStickyOverrideConfig;

    return {
      merged: mergedOverride,
      panel: extendedOverride?.panel || mergedOverride,
      content: extendedOverride?.body || mergedOverride,
      toolbar: extendedOverride?.toolbar || mergedOverride,
    };
  }

  /**
   * Manages filter opening state for sticky behavior.
   *
   * Toggles the 'boost-sd__sticky-opening' class on document.body based on
   * filter toggle button state. Sets up a MutationObserver to track button
   * class changes and DOM additions. Used to coordinate body scroll lock
   * and sticky positioning when filters are opened.
   *
   * @remarks
   * Only applies to specific configurations:
   * - Mobile: stickyFilterOnMobile + filterTreeMobileStyle === 'style1'
   * - Desktop: stickyFilterOnDesktop + horizontal layout + 'style-expand'
   *
   * The observer watches for class changes on toggle buttons and dynamically
   * added buttons. Automatically cleans up previous observers.
   *
   * @example
   * Override to add custom opening behavior:
   * ```typescript
   * blockFilterOpening() {
   *   super.blockFilterOpening();
   *   // Add custom class for shop-specific styling
   *   const { stickyFilterOnMobile, stickyFilterOnDesktop } = this.getFilterSettings();
   *   if (stickyFilterOnMobile || stickyFilterOnDesktop) {
   *     document.body.classList.add('custom-sticky-active');
   *   }
   * }
   * ```
   */
  public blockFilterOpening(): void {
    const {
      stickyFilterOnMobile,
      filterTreeMobileStyle,
      stickyFilterOnDesktop,
      filterLayout,
      filterTreeHorizontalStyle,
    } = this.getFilterSettings();

    if (!stickyFilterOnMobile && !stickyFilterOnDesktop) {
      return;
    }

    const isMobileSize = this.filterHelper.isMobile();
    const toggleButtonSelector = isMobileSize
      ? '.boost-sd__toolbar-top-mobile .boost-sd__filter-tree-toggle-button'
      : '.boost-sd__filter-tree-toggle-button--expand';

    const shouldApplyMobile = () => {
      return stickyFilterOnMobile && filterTreeMobileStyle === 'style1';
    };

    const shouldApplyDesktop = () => {
      return (
        stickyFilterOnDesktop &&
        filterLayout === 'horizontal' &&
        filterTreeHorizontalStyle === 'style-expand'
      );
    };

    const performToggleAction = (button: HTMLElement) => {
      if (shouldApplyDesktop() || shouldApplyMobile()) {
        if (button.classList.contains('boost-sd__filter-tree-toggle-button--active')) {
          document.body.classList.add('boost-sd__sticky-opening');
        } else {
          document.body.classList.remove('boost-sd__sticky-opening');
        }
      }
    };

    const checkAndPerformAction = () => {
      const filterTreeToggleButton = qs(toggleButtonSelector);
      if (filterTreeToggleButton) {
        performToggleAction(filterTreeToggleButton);
      }
    };

    // Initial action
    checkAndPerformAction();

    // Set up observer for the entire document to handle dynamic DOM changes
    if (shouldApplyDesktop() || shouldApplyMobile()) {
      // Clean up existing observer if any
      if (this._filterOpeningObserver) {
        this._filterOpeningObserver.disconnect();
      }

      const observer = new MutationObserver((mutations) => {
        let shouldCheck = false;

        mutations.forEach((mutation) => {
          // Check for added nodes that might contain our toggle button
          if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
            for (const node of Array.from(mutation.addedNodes)) {
              if (node.nodeType === Node.ELEMENT_NODE) {
                const element = node as Element;
                if (
                  element.matches?.(toggleButtonSelector) ||
                  element.querySelector?.(toggleButtonSelector)
                ) {
                  shouldCheck = true;
                  break;
                }
              }
            }
          }

          // Check for class changes on our target button
          if (
            mutation.type === 'attributes' &&
            mutation.attributeName === 'class' &&
            mutation.target instanceof HTMLElement &&
            mutation.target.matches(toggleButtonSelector)
          ) {
            performToggleAction(mutation.target);
          }
        });

        if (shouldCheck) {
          checkAndPerformAction();
        }
      });

      observer.observe(document.body, {
        attributes: true,
        childList: true,
        subtree: true,
        attributeFilter: ['class'],
      });

      this._filterOpeningObserver = observer;
    }
  }

  /**
   * Cleans up the filter opening observer.
   *
   * Disconnects the MutationObserver that tracks filter toggle button
   * state and DOM changes. Safe to call even if no observer exists.
   *
   * @remarks
   * Automatically called during controller cleanup. The observer is
   * created by blockFilterOpening() if needed.
   *
   * @example
   * Override to add observer cleanup tracking:
   * ```typescript
   * cleanupFilterOpeningObserver() {
   *   if (this._filterOpeningObserver) {
   *     // Log observer cleanup
   *     this.logObserverCleanup();
   *   }
   *   super.cleanupFilterOpeningObserver();
   * }
   * ```
   */
  public cleanupFilterOpeningObserver(): void {
    if (this._filterOpeningObserver) {
      this._filterOpeningObserver.disconnect();
    }
  }

  /**
   * Initializes mobile sticky behavior.
   *
   * Sets up sticky filter behavior for mobile layouts. Determines the
   * appropriate sticky implementation based on filterTreeMobileStyle
   * and stores the controller instance for later management.
   *
   * @returns Promise resolving to initialization result with controller and cleanup
   *
   * @remarks
   * Supports mobile styles:
   * - 'style1': Mobile expand style with full-screen overlay
   * - Other: Standard mobile sticky behavior
   *
   * @example
   * Override to add custom mobile initialization:
   * ```typescript
   * async initializeMobile() {
   *   // Add mobile-specific pre-initialization
   *   this.setupMobileScrollLock();
   *   const result = await super.initializeMobile();
   *   // Track mobile sticky initialization
   *   this.analytics.track('mobile_sticky_initialized');
   *   return result;
   * }
   * ```
   */
  public async initializeMobile() {
    const { filterTreeMobileStyle } = this.getFilterSettings();
    const { merged } = this.getStickyConfig('mobile', filterTreeMobileStyle);
    const context = this.appService.blockByType('filter'); //Depecated: use appService directly
    const init = initializeStickyMobile(context as any, merged);
    let result;

    switch (filterTreeMobileStyle) {
      case 'style1':
        result = await init.loadStickyFilterTreeMobileExpand();
        break;
      default:
        result = await init.loadStickyFilterTreeMobile();
        break;
    }

    if (result?.controller) {
      this.storeStickyInstance('mobile', filterTreeMobileStyle, result.controller, result.cleanup);
    }

    return result;
  }

  /**
   * Initializes vertical sticky behavior.
   *
   * Sets up sticky filter behavior for vertical layouts. Routes to the
   * appropriate implementation based on filterTreeVerticalStyle and
   * stores the controller instance.
   *
   * @returns Promise resolving to initialization result with controller and cleanup
   *
   * @remarks
   * Supports vertical styles:
   * - 'style-default': Standard vertical sticky
   * - 'style-expand': Expandable vertical with toolbar config
   * - Other: Off-canvas vertical style
   *
   * @example
   * Override to customize vertical initialization:
   * ```typescript
   * async initializeVertical() {
   *   const result = await super.initializeVertical();
   *   // Add custom vertical positioning
   *   this.adjustVerticalStickyPosition();
   *   return result;
   * }
   * ```
   */
  public async initializeVertical() {
    const { filterTreeVerticalStyle } = this.getFilterSettings();
    const { merged, toolbar } = this.getStickyConfig('vertical', filterTreeVerticalStyle);
    const context = this.appService.blockByType('filter'); //Depecated: use appService directly
    const init = initializeStickyVertical(context as any, merged);
    let result;

    switch (filterTreeVerticalStyle) {
      case 'style-default': {
        result = await init.loadStickyFilterTree();
        break;
      }
      case 'style-expand': {
        result = await init.loadStickyFilterTreeExpand(toolbar);
        break;
      }
      default: {
        result = await init.loadStickyFilterTreeOffCanvas();
        break;
      }
    }

    if (result?.controller) {
      this.storeStickyInstance(
        'vertical',
        filterTreeVerticalStyle,
        result.controller,
        result.cleanup
      );
    }

    return result;
  }

  /**
   * Initializes horizontal sticky behavior.
   *
   * Sets up sticky filter behavior for horizontal layouts. Routes to the
   * appropriate implementation based on filterTreeHorizontalStyle and
   * stores the controller instance.
   *
   * @returns Promise resolving to initialization result with controller and cleanup
   *
   * @remarks
   * Supports horizontal styles:
   * - 'style-expand': Horizontal toolbar with expand behavior
   * - Other: Standard horizontal sticky
   *
   * @example
   * Override to customize horizontal initialization:
   * ```typescript
   * async initializeHorizontal() {
   *   const result = await super.initializeHorizontal();
   *   // Adjust horizontal spacing for shop header
   *   this.adjustHorizontalOffset();
   *   return result;
   * }
   * ```
   */
  public async initializeHorizontal() {
    const { filterTreeHorizontalStyle } = this.getFilterSettings();
    const { merged, toolbar } = this.getStickyConfig('horizontal', filterTreeHorizontalStyle);
    const context = this.appService.blockByType('filter'); //Depecated: use appService directly
    const init = initializeStickyHorizontal(context as any, merged);
    let result;

    switch (filterTreeHorizontalStyle) {
      case 'style-expand': {
        result = await init.loadStickyFilterTreeHorizontalToolbar(toolbar);
        break;
      }
      default: {
        result = await init.loadStickyFilterTreeHorizontal();
        break;
      }
    }

    if (result?.controller) {
      this.storeStickyInstance(
        'horizontal',
        filterTreeHorizontalStyle,
        result.controller,
        result.cleanup
      );
    }

    return result;
  }

  /**
   * Initializes appropriate sticky behavior based on context.
   *
   * Main initialization method that determines the correct sticky
   * implementation based on viewport size, sticky settings, and layout.
   * Routes to mobile, vertical, or horizontal initialization.
   *
   * @returns Promise resolving to initialization result, or undefined if sticky disabled
   *
   * @remarks
   * Initialization logic:
   * - Returns early if both mobile and desktop sticky are disabled
   * - Mobile size + stickyFilterOnMobile → initializeMobile()
   * - Desktop size + stickyFilterOnDesktop + horizontal layout → initializeHorizontal()
   * - Desktop size + stickyFilterOnDesktop + other layouts → initializeVertical()
   *
   * @example
   * Override to add custom initialization logic:
   * ```typescript
   * async initialize() {
   *   // Pre-initialization setup
   *   await this.prepareCustomStickyElements();
   *   const result = await super.initialize();
   *   if (result) {
   *     // Track successful initialization
   *     this.analytics.track('sticky_initialized', {
   *       layout: this.getFilterSettings().filterLayout,
   *       viewport: this.filterHelper.isMobile() ? 'mobile' : 'desktop'
   *     });
   *   }
   *   return result;
   * }
   * ```
   */
  public async initialize() {
    const { stickyFilterOnMobile, stickyFilterOnDesktop, filterLayout } = this.getFilterSettings();

    if (!stickyFilterOnMobile && !stickyFilterOnDesktop) {
      return;
    }

    const isMobileSize = this.filterHelper.isMobile();

    if (isMobileSize && stickyFilterOnMobile) {
      return await this.initializeMobile();
    }

    if (!isMobileSize && stickyFilterOnDesktop) {
      if (filterLayout === 'horizontal') {
        return await this.initializeHorizontal();
      }
      return await this.initializeVertical();
    }
  }

  /**
   * Complete cleanup of all sticky instances and observers.
   *
   * Main cleanup method that removes all sticky controller instances
   * and disconnects the filter opening observer. Should be called when
   * the controller is no longer needed.
   *
   * @remarks
   * Safe to call multiple times. Automatically called during filter cleanup.
   *
   * @example
   * Override to add custom cleanup:
   * ```typescript
   * cleanup() {
   *   // Clean custom shop-specific sticky elements
   *   this.cleanupCustomStickyElements();
   *   super.cleanup();
   *   // Track cleanup for monitoring
   *   this.analytics.track('sticky_controller_cleaned');
   * }
   * ```
   */
  public cleanup(): void {
    this.cleanupAllStickyInstances();
    this.cleanupFilterOpeningObserver();
  }

  /**
   * Loads sticky content for vertical expand layout.
   *
   * Delegates to sticky-dynamic module to load and configure sticky
   * content for vertical expand style filters.
   *
   * @returns Result from loadStickyFilterTreeExpandContent
   *
   * @example
   * Override to add custom content loading:
   * ```typescript
   * loadStickyFilterTreeExpandContent() {
   *   const result = super.loadStickyFilterTreeExpandContent();
   *   // Add custom content elements
   *   this.addCustomExpandContent();
   *   return result;
   * }
   * ```
   */
  loadStickyFilterTreeExpandContent() {
    const block = this.appService.blockByType('filter');
    const { filterTreeVerticalStyle } = this.appService.filterSettings;
    const stickyConfig = this.getStickyConfig('vertical', filterTreeVerticalStyle);
    return loadStickyFilterTreeExpandContent(block as any, stickyConfig.content);
  }

  /**
   * Loads sticky panel and content for horizontal expand layout.
   *
   * Delegates to sticky-dynamic module to load both panel and content
   * configurations for horizontal expand style filters.
   *
   * @example
   * Override to customize horizontal expand loading:
   * ```typescript
   * loadStickyFilterTreeHorizontalExpandContent() {
   *   super.loadStickyFilterTreeHorizontalExpandContent();
   *   // Add custom horizontal elements
   *   this.enhanceHorizontalLayout();
   * }
   * ```
   */
  loadStickyFilterTreeHorizontalExpandContent() {
    const block = this.appService.blockByType('filter');
    const { filterTreeHorizontalStyle } = this.appService.filterSettings;

    const stickyConfig = this.getStickyConfig('horizontal', filterTreeHorizontalStyle);
    loadStickyFilterTreeHorizontalPanel(block as any, stickyConfig.panel);
    loadStickyFilterTreeHorizontalContent(block as any, stickyConfig.content);
  }

  /**
   * Loads sticky content for mobile expand layout.
   *
   * Delegates to sticky-dynamic module to load and configure sticky
   * content for mobile expand style filters when sticky is enabled.
   *
   * @remarks
   * Only loads if stickyFilterOnMobile is enabled in settings.
   *
   * @example
   * Override to add mobile-specific content:
   * ```typescript
   * loadStickyFilterTreeMobileExpandContent() {
   *   super.loadStickyFilterTreeMobileExpandContent();
   *   // Add mobile-specific UI elements
   *   this.addMobileFilterEnhancements();
   * }
   * ```
   */
  loadStickyFilterTreeMobileExpandContent() {
    const block = this.appService.blockByType('filter');
    const { stickyFilterOnMobile, filterTreeMobileStyle } = this.appService.filterSettings;
    if (stickyFilterOnMobile) {
      // Get merged configuration for mobile expand sticky call
      const stickyConfig = this.getStickyConfig('mobile', filterTreeMobileStyle);
      loadStickyFilterTreeMobileExpandContent(block as any, stickyConfig.content);
    }
  }
}
